import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { AppNotification } from '@conference/shared';
import { notificationService } from '../services/dataService';
import { notify } from '../utils/notifications';
import { useApp } from './AppContext';
import { useEventData } from './EventDataContext';

interface NotificationContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

const EMPTY_NOTIFICATION_CONTEXT: NotificationContextValue = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  refresh: async () => undefined,
  markRead: async () => undefined,
  markAllRead: async () => undefined,
};

const NotificationContext = createContext<NotificationContextValue>(EMPTY_NOTIFICATION_CONTEXT);

export function NotificationProvider({
  children,
  pollMs = 30_000,
}: {
  children: ReactNode;
  pollMs?: number;
}) {
  const { authUser, authSession, isAuthSessionCurrent, policyStatus } = useApp();
  const { preview } = useEventData();
  const userId = authUser?.id ?? null;
  const policyAccepted = policyStatus?.accepted === true;
  const requestScope = useMemo(
    () => ({ authSession, policyAccepted, userId }),
    [authSession, policyAccepted, userId],
  );
  const latestRequestScope = useRef(requestScope);
  latestRequestScope.current = requestScope;
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const initializedUserId = useRef<string | null>(null);
  const knownIds = useRef<Set<string>>(new Set());
  const refreshInFlight = useRef<typeof requestScope | null>(null);

  const refresh = useCallback(async () => {
    const owner = authSession;
    if (
      preview
      || !policyAccepted
      || !userId
      || !owner
      || !isAuthSessionCurrent(owner)
      || refreshInFlight.current === requestScope
    ) return;

    refreshInFlight.current = requestScope;
    setLoading(true);
    try {
      const next = await notificationService.getAll(userId, { signal: owner.signal });
      if (!isAuthSessionCurrent(owner) || latestRequestScope.current !== requestScope) return;

      const isInitialLoad = initializedUserId.current !== userId;
      if (isInitialLoad) {
        initializedUserId.current = userId;
      } else {
        const arrivals = next.filter((item) => !knownIds.current.has(item.id));
        if (arrivals.length === 1) {
          notify.info(arrivals[0].title, arrivals[0].message);
        } else if (arrivals.length > 1) {
          notify.info(`${arrivals.length} new announcements`, arrivals[0].title);
        }
      }

      knownIds.current = new Set(next.map((item) => item.id));
      setNotifications(next);
    } catch {
      // Offline or temporarily unavailable: preserve the last known feed.
    } finally {
      if (refreshInFlight.current === requestScope) refreshInFlight.current = null;
      if (isAuthSessionCurrent(owner) && latestRequestScope.current === requestScope) {
        setLoading(false);
      }
    }
  }, [authSession, isAuthSessionCurrent, policyAccepted, preview, requestScope, userId]);

  const markRead = useCallback(async (id: string) => {
    const owner = authSession;
    if (!policyAccepted || !owner || !isAuthSessionCurrent(owner)) return;
    setNotifications((current) => current.map((item) => (
      item.id === id ? { ...item, read: true } : item
    )));
    try {
      await notificationService.markRead(id, { signal: owner.signal });
    } catch {
      await refresh();
    }
  }, [authSession, isAuthSessionCurrent, policyAccepted, refresh]);

  const markAllRead = useCallback(async () => {
    const owner = authSession;
    if (!policyAccepted || !owner || !userId || !isAuthSessionCurrent(owner)) return;
    setNotifications((current) => current.map((item) => ({ ...item, read: true })));
    try {
      await notificationService.markAllRead(userId, { signal: owner.signal });
    } catch {
      await refresh();
    }
  }, [authSession, isAuthSessionCurrent, policyAccepted, refresh, userId]);

  useEffect(() => {
    initializedUserId.current = null;
    knownIds.current = new Set();
    setNotifications([]);

    if (
      preview
      || !policyAccepted
      || !userId
      || !authSession
      || !isAuthSessionCurrent(authSession)
    ) {
      setLoading(false);
      return;
    }

    void refresh();
    const timer = window.setInterval(() => { void refresh(); }, pollMs);
    const onFocus = () => { void refresh(); };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [authSession, isAuthSessionCurrent, policyAccepted, pollMs, preview, refresh, userId]);

  const value = useMemo<NotificationContextValue>(() => ({
    notifications,
    unreadCount: notifications.filter((item) => !item.read).length,
    loading,
    refresh,
    markRead,
    markAllRead,
  }), [loading, markAllRead, markRead, notifications, refresh]);

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  return useContext(NotificationContext);
}
